/**
 * SAT API - Handles all API calls to the assessment backend (v2).
 *
 * Every endpoint wraps its payload in { status, data } except /upload-image,
 * which returns img_url at the top level. `request` normalises both.
 */
(function (global) {
  "use strict";

  var config = {
    apiBaseUrl: "",
    customerId: null,
  };

  // ------------------------------------------------------------------ debug log
  // The flow spans three page loads, so the console is cleared between steps.
  // Every call is mirrored into sessionStorage and readable at any point via
  // window.SATDebug.dump().
  var LOG_KEY = "satApiLog";
  var LOG_LIMIT = 50;

  // The one debug switch for the whole SAT flow: ?satDebug=true for this page,
  // or localStorage.setItem('satDebug', 'true') to persist across navigations.
  // Exposed below as SATDebug.enabled() so the other modules read this check
  // rather than keeping a copy of it.
  //
  // The query parameter is read once and remembered: the flow rewrites its own
  // URL as the user moves between screens (?screen=..., ?step=...), so a live
  // read would lose the parameter - and the switch with it - on the first
  // navigation. localStorage is re-read on every call, so it can be flipped
  // from the console without a reload.
  var debugParam = (function () {
    try {
      return (
        new URLSearchParams(global.location.search).get("satDebug") === "true"
      );
    } catch (e) {
      return false;
    }
  })();

  function debugEnabled() {
    if (debugParam) return true;
    try {
      return global.localStorage.getItem("satDebug") === "true";
    } catch (e) {
      return false;
    }
  }

  function readLog() {
    try {
      return JSON.parse(global.sessionStorage.getItem(LOG_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function writeLog(entries) {
    try {
      global.sessionStorage.setItem(
        LOG_KEY,
        JSON.stringify(entries.slice(-LOG_LIMIT)),
      );
    } catch (e) {
      /* quota - keep going, the console log still fired */
    }
  }

  function describeBody(body) {
    if (!body) return null;

    if (typeof FormData !== "undefined" && body instanceof FormData) {
      var parts = [];
      body.forEach(function (value, key) {
        parts.push(
          value && value.name !== undefined
            ? {
                field: key,
                filename: value.name,
                type: value.type,
                size: value.size,
              }
            : { field: key, value: String(value) },
        );
      });
      return { type: "multipart/form-data", parts: parts };
    }

    try {
      return JSON.parse(body);
    } catch (e) {
      return String(body).slice(0, 500);
    }
  }

  function record(entry) {
    var entries = readLog();
    entries.push(entry);
    writeLog(entries);

    // A failure always surfaces; a successful call is noise on a customer's
    // console. Nothing is lost by staying quiet - the entry went into the log
    // above either way, and SATDebug.dump() still has every call this session.
    if (entry.ok && !debugEnabled()) return;

    var label =
      "[SAT API] " + entry.method + " " + entry.path + " -> " + entry.status;
    if (entry.ok) {
      console.groupCollapsed(label + " (" + entry.durationMs + "ms)");
    } else {
      console.group(
        "%c" + label + " (" + entry.durationMs + "ms)",
        "color:#c00",
      );
    }
    console.log("url:", entry.url);
    console.log("request headers:", entry.requestHeaders);
    console.log("request body:", entry.requestBody);
    console.log("response:", entry.response);
    if (entry.error) console.error("error:", entry.error);
    console.groupEnd();
  }

  global.SATDebug = {
    // Read by the other SAT modules so the flow has one debug switch instead
    // of the same check copied into each file.
    enabled: debugEnabled,
    log: readLog,
    dump: function () {
      var entries = readLog();
      console.group("[SAT API] " + entries.length + " call(s) this session");
      if (console.table) {
        console.table(
          entries.map(function (e) {
            return {
              at: e.at,
              method: e.method,
              path: e.path,
              status: e.status,
              ok: e.ok,
              ms: e.durationMs,
            };
          }),
        );
      }
      entries.forEach(function (e) {
        console.log(e.method, e.path, e.status, e);
      });
      console.groupEnd();
      return entries;
    },
    json: function () {
      return JSON.stringify(readLog(), null, 2);
    },
    clear: function () {
      try {
        global.sessionStorage.removeItem(LOG_KEY);
      } catch (e) {}
    },
  };

  function customerGid() {
    if (!config.customerId) return null;
    var id = String(config.customerId);
    return id.indexOf("gid://") === 0 ? id : "gid://shopify/Customer/" + id;
  }

  function buildHeaders(extra) {
    var headers = {
      "x-source": "web-app",
      "x-platform": "web",
    };

    if (typeof global.getDeviceId === "function") {
      headers["x-device-id"] = global.getDeviceId();
    }

    var gid = customerGid();
    if (gid) headers["x-customer-id"] = gid;

    if (extra) {
      Object.keys(extra).forEach(function (key) {
        headers[key] = extra[key];
      });
    }

    return headers;
  }

  /**
   * Reads both documented error shapes: Joi validation failures return
   * { code, message, errors[] }, controller failures return { status, message }.
   */
  function errorMessage(json, response) {
    if (json) {
      if (json.errors && json.errors.length) {
        var first = json.errors[0];
        if (first.messages && first.messages.length) return first.messages[0];
      }
      if (json.message) return json.message;
    }
    return "Request failed (" + response.status + ")";
  }

  async function request(path, options) {
    options = options || {};

    var method = options.method || "GET";
    var url = config.apiBaseUrl + path;
    var headers = buildHeaders(options.headers);
    var startedAt = Date.now();

    var entry = {
      at: new Date().toISOString(),
      method: method,
      path: path,
      url: url,
      requestHeaders: headers,
      requestBody: describeBody(options.body),
    };

    var response;
    try {
      response = await fetch(url, {
        method: method,
        headers: headers,
        body: options.body,
        signal: options.signal,
      });
    } catch (err) {
      if (err.name === "AbortError") throw err;
      entry.status = "network-error";
      entry.ok = false;
      entry.durationMs = Date.now() - startedAt;
      entry.error = err.message;
      record(entry);
      throw err;
    }

    var json = null;
    try {
      json = await response.json();
    } catch (e) {
      json = null;
    }

    entry.status = response.status;
    entry.ok = response.ok;
    entry.durationMs = Date.now() - startedAt;
    entry.response = json;
    record(entry);

    if (!response.ok) {
      throw new Error(errorMessage(json, response));
    }

    if (!json) return null;
    return json.data !== undefined ? json.data : json;
  }

  function jsonBody(payload) {
    return {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    };
  }

  /**
   * Uploads a single capture file. `source` flags the image as the raw selfie
   * or the annotated zone map. Content-Type is deliberately left unset so the
   * browser generates the multipart boundary.
   */
  async function uploadImage(fileEntry, source) {
    var formData = new FormData();
    formData.append(
      fileEntry.field || "file",
      fileEntry.blob,
      fileEntry.filename || "selfie.jpg",
    );

    var data = await request("/upload-image", {
      method: "POST",
      headers: { "image-source": source },
      body: formData,
    });

    return data && data.img_url;
  }

  global.SATAPI = {
    configure: function (opts) {
      if (!opts) return;
      if (opts.apiBaseUrl) config.apiBaseUrl = opts.apiBaseUrl;
      if (opts.customerId !== undefined) config.customerId = opts.customerId;
    },

    /**
     * Uploads the capture payload's files. The annotated zone map is optional;
     * when the widget could not produce one we fall back to the raw image so
     * /report still receives the analyzed_image it requires.
     */
    uploadCapture: async function (payload, onProgress) {
      var files = (payload && payload.files) || [];

      var plain = files.find(function (file) {
        return file.role === "plain";
      });
      var annotated = files.find(function (file) {
        return file.role === "annotated";
      });

      if (!plain) throw new Error("No photo to upload. Please retake.");

      onProgress && onProgress("Uploading your photo...", 10);
      var rawImage = await uploadImage(plain, "raw");

      var analyzedImage = null;
      if (annotated) {
        onProgress && onProgress("Uploading skin map...", 25);
        analyzedImage = await uploadImage(annotated, "analyzed");
      }

      onProgress && onProgress("Photo uploaded", 40);

      var urls = {
        raw_image: rawImage,
        analyzed_image: analyzedImage || rawImage,
      };

      // These URLs must be publicly fetchable by both the browser and the
      // backend's AI gateway - a host that does not resolve shows up as a
      // broken image here and a URL_ERROR_DNS from /scores.
      if (debugEnabled()) console.log("[SAT API] uploaded image URLs:", urls);

      return urls;
    },

    getScores: function (imgUrl, options) {
      options = options || {};
      var init = jsonBody({ img_url: imgUrl });
      init.method = "POST";
      init.signal = options.signal;
      return request("/scores", init);
    },

    /**
     * The doc's field table says the customer id travels as a header while its
     * own curl example puts it in the body. We send both until the backend
     * confirms which one it reads.
     */
    generateReport: async function (payload, options) {
      options = options || {};

      var body = Object.assign({}, payload);
      var gid = customerGid();
      if (gid) body.customer_id = gid;

      var init = jsonBody(body);
      init.method = "POST";
      init.signal = options.signal;

      // /report nests one level deeper than the other endpoints: its envelope
      // unwraps to { report: {...} }.
      var data = await request("/report", init);
      return data && data.report ? data.report : data;
    },

    getAssessmentList: function () {
      return request("/assessment-list", { method: "GET" });
    },

    getAssessment: function (assessmentId) {
      return request("/" + assessmentId, { method: "GET" });
    },
  };
})(window);
